import React, { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@love-irish/ui-components/dialog"
import { Input } from "@love-irish/ui-components/input"
import Button from "@love-irish/core-button"
import {
  IconBrandX,
  IconBrandFacebook,
  IconBrandWhatsapp,
  IconBrandReddit,
  IconBrandBluesky,
  IconMail,
  IconCopy,
  IconCheck,
  IconMarkdown,
} from "@tabler/icons-react"
import type { Icon } from "@tabler/icons-react"

export interface ShareModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The URL to share (absolute). */
  url: string
  /** Optional resource title, shown in the header and used as share text. */
  title?: string
  /** Optional share text; falls back to `title`. */
  text?: string
  /** Optional Markdown (.md) alternate — surfaced as an "for AI/LLMs" link. */
  markdownUrl?: string
}

interface ShareTarget {
  label: string
  icon: Icon
  /** Builds the share-intent URL from the already-encoded url + text. */
  href: (url: string, text: string) => string
}

// Each target opens its web share-intent in a new tab. `url`/`text` arrive URL-encoded.
const TARGETS: ShareTarget[] = [
  {
    label: "X",
    icon: IconBrandX,
    href: (u, t) => `https://twitter.com/intent/tweet?url=${u}&text=${t}`,
  },
  {
    label: "Facebook",
    icon: IconBrandFacebook,
    href: (u) => `https://www.facebook.com/sharer/sharer.php?u=${u}`,
  },
  {
    label: "WhatsApp",
    icon: IconBrandWhatsapp,
    href: (u, t) => `https://wa.me/?text=${t}%20${u}`,
  },
  {
    label: "Reddit",
    icon: IconBrandReddit,
    href: (u, t) => `https://www.reddit.com/submit?url=${u}&title=${t}`,
  },
  {
    label: "Bluesky",
    icon: IconBrandBluesky,
    href: (u, t) => `https://bsky.app/intent/compose?text=${t}%20${u}`,
  },
  {
    label: "Email",
    icon: IconMail,
    href: (u, t) => `mailto:?subject=${t}&body=${u}`,
  },
]

/**
 * A share modal: a row of social share targets plus a read-only link field with a
 * copy-to-clipboard button (shows a "Copied" confirmation). Controlled via `open` /
 * `onOpenChange`, so any surface can open it.
 */
export const ShareModal: React.FC<ShareModalProps> = ({
  open,
  onOpenChange,
  url,
  title,
  text,
  markdownUrl,
}) => {
  const [copied, setCopied] = useState(false)

  const encodedUrl = encodeURIComponent(url)
  const encodedText = encodeURIComponent(text || title || "")

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard may be unavailable (e.g. insecure context); no-op.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>{title ? `Share "${title}"` : "Share"}</DialogTitle>
          <DialogDescription>
            Share this with friends, or copy the link.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-3 py-2">
          {TARGETS.map((target) => {
            const Ico = target.icon
            return (
              <a
                key={target.label}
                href={target.href(encodedUrl, encodedText)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Share on ${target.label}`}
                className="flex flex-col items-center gap-1 text-xs text-secondary-600 hover:text-foreground"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-200">
                  <Ico size={20} />
                </span>
                {target.label}
              </a>
            )
          })}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Input
            readOnly
            value={url}
            aria-label="Shareable link"
            onFocus={(e) => e.currentTarget.select()}
          />
          <Button size="sm" onClick={handleCopy} aria-label="Copy link">
            {copied ? (
              <>
                <IconCheck size={16} className="mr-1 inline-block" />
                Copied
              </>
            ) : (
              <>
                <IconCopy size={16} className="mr-1 inline-block" />
                Copy
              </>
            )}
          </Button>
        </div>

        {markdownUrl && (
          <a
            href={markdownUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 pt-1 text-sm text-secondary-600 hover:text-foreground"
          >
            <IconMarkdown size={16} />
            View the Markdown version (for AI / LLMs)
          </a>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default ShareModal
